import React, { useState, useMemo } from "react";
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { format, startOfDay, isAfter, isBefore, isSameDay } from "date-fns";
import { useTheme } from "../../../context/ThemeContext";

const EventList = ({ events, selectedDate, onEventSelect }) => {
	const [filter, setFilter] = useState("all"); // all, upcoming, past
	const [showCreateForm, setShowCreateForm] = useState(false);
	const theme = useTheme();
	const styles = useStyles();

	const filteredEvents = useMemo(() => {
		const today = startOfDay(new Date());
		let filtered = events || [];

		// Apply date filters
		switch (filter) {
			case "upcoming":
				filtered = filtered.filter((event) => {
					const eventDate = startOfDay(new Date(event.date));
					return isAfter(eventDate, today) || isSameDay(eventDate, today);
				});
				break;
			case "past":
				filtered = filtered.filter((event) => {
					const eventDate = startOfDay(new Date(event.date));
					return isBefore(eventDate, today);
				});
				break;
			default:
				// For "all" filter, show events for the selected date
				filtered = filtered.filter((event) => {
					const eventDate = startOfDay(new Date(event.date));
					const checkDate = startOfDay(selectedDate);
					return isSameDay(eventDate, checkDate);
				});
				break;
		}

		// Sort events by time
		return filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
	}, [events, selectedDate, filter]);

	const renderEventCard = (event) => (
		<TouchableOpacity key={event.id} style={styles.eventCard} onPress={() => onEventSelect(event)}>
			<View style={styles.eventTime}>
				<Text style={styles.timeText}>{format(new Date(event.date), "h:mm a")}</Text>
			</View>

			<View style={styles.eventContent}>
				<View style={styles.eventHeader}>
					<Text style={styles.eventTitle}>{event.title}</Text>
					{event.isPaid && (
						<View style={styles.paidBadge}>
							<Text style={styles.paidText}>Paid</Text>
						</View>
					)}
				</View>

				<View style={styles.eventDetails}>
					<View style={styles.detailItem}>
						<Ionicons name="location-outline" size={16} color={theme.colors.textSecondary} />
						<Text style={styles.detailText}>{event.location}</Text>
					</View>
					<View style={styles.detailItem}>
						<Ionicons name="people-outline" size={16} color={theme.colors.textSecondary} />
						<Text style={styles.detailText}>{event.attendees?.length || 0}</Text>
					</View>
				</View>

				{event.tags && false && (
					<View style={styles.tags}>
						{event.tags.map((tag) => (
							<View key={tag} style={styles.tag}>
								<Text style={styles.tagText}>{tag}</Text>
							</View>
						))}
					</View>
				)}
			</View>
		</TouchableOpacity>
	);

	return (
		<View style={styles.container}>
			<View style={styles.header}>
				<View style={styles.headerContent}>
					<View style={styles.filterButtons}>
						<TouchableOpacity style={[styles.filterButton, filter === "all" && styles.activeFilter]} onPress={() => setFilter("all")}>
							<Text style={[styles.filterText, filter === "all" && styles.activeFilterText]}>All</Text>
						</TouchableOpacity>
						<TouchableOpacity style={[styles.filterButton, filter === "upcoming" && styles.activeFilter]} onPress={() => setFilter("upcoming")}>
							<Text style={[styles.filterText, filter === "upcoming" && styles.activeFilterText]}>Upcoming</Text>
						</TouchableOpacity>
						<TouchableOpacity style={[styles.filterButton, filter === "past" && styles.activeFilter]} onPress={() => setFilter("past")}>
							<Text style={[styles.filterText, filter === "past" && styles.activeFilterText]}>Past</Text>
						</TouchableOpacity>
					</View>
					<TouchableOpacity style={styles.createButton} onPress={() => setShowCreateForm(true)}>
						<Ionicons name="add" size={20} color={theme.colors.systemBackground.primary} />
						<Text style={styles.createButtonText}>Create</Text>
					</TouchableOpacity>
				</View>
			</View>

			<ScrollView style={styles.eventsList}>
				{filteredEvents.length > 0 ? (
					filteredEvents.map(renderEventCard)
				) : (
					<View style={styles.noEvents}>
						<Text style={styles.noEventsText}>No events found for this date</Text>
					</View>
				)}
			</ScrollView>
		</View>
	);
};

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		container: {
			flex: 1,
			backgroundColor: theme.colors.systemBackground.primary,
		},
		header: {
			padding: theme.spacing.md,
			borderBottomWidth: 1,
			borderBottomColor: theme.colors.separator,
			backgroundColor: theme.colors.systemBackground.secondary,
		},
		title: {
			...theme.typography.title3,
			color: theme.colors.text,
			marginBottom: theme.spacing.sm,
		},
		headerContent: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
		},
		filterButtons: {
			flexDirection: "row",
			gap: theme.spacing.sm,
		},
		filterButton: {
			paddingHorizontal: theme.spacing.sm,
			paddingVertical: theme.spacing.xs,
			borderRadius: theme.borderRadius.pill,
			backgroundColor: theme.colors.gray[6],
		},
		activeFilter: {
			backgroundColor: theme.colors.primary,
		},
		filterText: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
		},
		activeFilterText: {
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
		eventsList: {
			flex: 1,
		},
		eventCard: {
			flexDirection: "row",
			padding: theme.spacing.md,
			borderBottomWidth: 1,
			borderBottomColor: theme.colors.separator,
			backgroundColor: theme.colors.systemBackground.primary,
		},
		eventTime: {
			width: 80,
			marginRight: theme.spacing.sm,
		},
		timeText: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
			fontWeight: "500",
		},
		eventContent: {
			flex: 1,
		},
		eventHeader: {
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "space-between",
			marginBottom: theme.spacing.sm,
		},
		eventTitle: {
			...theme.typography.headline,
			color: theme.colors.text,
			flex: 1,
		},
		paidBadge: {
			backgroundColor: theme.colors.systemYellow,
			paddingHorizontal: theme.spacing.sm,
			paddingVertical: theme.spacing.xs,
			borderRadius: theme.borderRadius.pill,
			marginLeft: theme.spacing.sm,
		},
		paidText: {
			...theme.typography.caption2,
			color: theme.colors.text,
			fontWeight: "500",
		},
		eventDetails: {
			flexDirection: "row",
			gap: theme.spacing.md,
			marginBottom: theme.spacing.sm,
		},
		detailItem: {
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing.xs,
		},
		detailText: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
		},
		tags: {
			flexDirection: "row",
			flexWrap: "wrap",
			gap: theme.spacing.xs,
		},
		tag: {
			backgroundColor: theme.colors.gray[6],
			paddingHorizontal: theme.spacing.sm,
			paddingVertical: theme.spacing.xs,
			borderRadius: theme.borderRadius.pill,
		},
		tagText: {
			...theme.typography.caption2,
			color: theme.colors.textSecondary,
		},
		noEvents: {
			padding: theme.spacing.md,
			alignItems: "center",
		},
		noEventsText: {
			...theme.typography.body,
			color: theme.colors.textSecondary,
		},
		createButton: {
			flexDirection: "row",
			alignItems: "center",
			backgroundColor: theme.colors.primary,
			paddingHorizontal: theme.spacing.sm,
			paddingVertical: theme.spacing.xs,
			borderRadius: theme.borderRadius.pill,
			gap: theme.spacing.xs,
		},
		createButtonText: {
			...theme.typography.subhead,
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
		detailsOverlay: {
			position: "absolute",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			backgroundColor: theme.colors.systemBackground.primary,
			zIndex: 1000,
			...theme.shadows.lg,
		},
	});
};

export default EventList;
